/* SDÍLENÝ SLOVNÍK UI TEXTŮ — klíčem je český text.
 * ============================================================================
 *
 * PROČ EXISTUJE (9. 9. 2026, zadání majitele: „přelož vše, ve všech jazycích")
 * --------------------------------------------------------------------------
 * V repu bylo přes 1 300 dvoujazyčných ternárů:
 *
 *     SK ? "Uložiť" : "Uložit"
 *
 * Ternár zná JEN DVA jazyky. Vietnamec, Ukrajinec i Angličan spadli do české
 * větve — a protože se to nikde nevyhodí jako chyba, appka prostě mluvila
 * česky na lidi, kteří česky neumí.
 *
 * PROČ SI JAZYK BERE SÁM (a nedostává ho parametrem)
 * -------------------------------------------------
 * Ternáry jsou roztroušené v padesáti souborech, uvnitř pomocných funkcí,
 * callbacků a komponent, kde `lang` z `useLanguage()` často NENÍ v dosahu.
 * Předávat ho parametrem by znamenalo měnit signatury desítek funkcí a
 * protahovat prop skrz celý strom — velký, rizikový diff kvůli jednomu
 * řetězci. Jazyk UI je navíc skutečně globální stav aplikace (jeden pro celou
 * obrazovku), takže ho drží tenhle modul a `TL("text")` je čistá náhrada
 * jednoho výrazu za jiný.
 *
 * Týž vzor už v repu je: `AnalyzaV2Page` má modulové `LANG` a `tenantMena.js`
 * externí store pro zemi tenanta.
 *
 * KDO JAZYK NASTAVUJE
 * -------------------
 * `LanguageProvider` volá `nastavJazykUI(lang)` při startu i při každé změně.
 * Než se tak stane, čte se `localStorage` (týž klíč `bp_lang`), takže i první
 * render před připojením provideru je ve správném jazyce.
 *
 * CO SE STANE PŘI CHYBĚJÍCÍM PŘEKLADU
 * -----------------------------------
 * Vykreslí se ČEŠTINA. Nikdy prázdno, nikdy holý klíč. Neúplný řádek tedy
 * appku nerozbije — jen ji na tom místě nechá česky, což je přesně dnešní
 * stav, ne zhoršení.
 */
import { PREKLADY_UI } from "@/data/prekladyUI.data";
/* Slovenština pro Diář/Tým/Nábor/Rezervace (18. 10. 2026, „slovensky, eura, vše
 * ať sedí“). Samostatný soubor, protože prekladyUI.data.js souběžně mění jiné
 * týmy. Řádky z velkého slovníku mají přednost (jsou až za ním). */
import { PREKLADY_DIAR_TYM_NABOR } from "@/data/prekladyUI.diarTymNabor.data";
/* Nastavení rezervací „jako Calendly“ (19. 10. 2026) — vlastní soubor ze stejného důvodu. */
import { PREKLADY_REZERVACE } from "@/data/prekladyUI.rezervace.data";
/* Nastavení → Můj web slovensky (19. 10. 2026, NE-09) — vlastní soubor ze stejného důvodu. */
import { PREKLADY_NASTAVENI_WEB } from "@/data/prekladyUI.nastaveniWeb.data";
/* Pipeline klientů: rychlé přidání + stopa + plánování (20. 10. 2026) — vlastní soubor ze stejného důvodu. */
import { PREKLADY_PIPELINE_RYCHLE } from "@/data/prekladyUI.pipelineRychle.data";
/* Kalkulačky slovensky — Historická (vedlejší záložky) a kino strategií (24. 10. 2026) — vlastní soubor ze stejného důvodu. */
import { PREKLADY_KALKULACKY } from "@/data/prekladyUI.kalkulacky.data";
/* Trychtýře + proužek náhledu slovensky (24. 10. 2026, podnět beplan.sk) — vlastní soubor ze stejného důvodu. */
import { PREKLADY_LIEVIKY } from "@/data/prekladyUI.lieviky.data";
import { PREKLADY_OVLADANI } from "@/data/prekladyUI.ovladani.data";
/* Poznámky enginu v klientských výstupech (24. 10. 2026, „výstupy pro klienty doladit vše“) — vlastní soubor ze stejného důvodu. */
import { PREKLADY_VYSTUPY } from "@/data/prekladyUI.vystupy.data";
/* Interní stránky slovensky — Projekt 100, Finanční mapa, Leadovač (25. 10. 2026, velký test SK) — vlastní soubor ze stejného důvodu. */
import { PREKLADY_SK2 } from "@/data/prekladyUI.sk2.data";
/* Můj byznys plán (25. 10. 2026, N4) — vlastní soubor ze stejného důvodu. */
import { PREKLADY_BYZNYS_PLAN } from "@/data/prekladyUI.byznysPlan.data";

const SLOVNIK = { ...PREKLADY_BYZNYS_PLAN, ...PREKLADY_SK2,...PREKLADY_OVLADANI, ...PREKLADY_VYSTUPY, ...PREKLADY_LIEVIKY, ...PREKLADY_KALKULACKY, ...PREKLADY_PIPELINE_RYCHLE, ...PREKLADY_NASTAVENI_WEB, ...PREKLADY_REZERVACE, ...PREKLADY_DIAR_TYM_NABOR, ...PREKLADY_UI };

/** Jazyky, které appka umí. Cokoli mimo → čeština. */
export const JAZYKY = ["cs", "sk", "en", "uk", "vi"];

function zLocalStorage() {
  try {
    const l = window.localStorage.getItem("bp_lang");
    return JAZYKY.includes(l) ? l : "cs";
  } catch {
    return "cs";   // privátní režim, SSR, zakázané úložiště
  }
}

let JAZYK = zLocalStorage();

/** Nastaví jazyk UI. Volá `LanguageProvider`; neznámý kód → čeština. */
export function nastavJazykUI(jazyk) {
  JAZYK = JAZYKY.includes(jazyk) ? jazyk : "cs";
}

/** Aktuální jazyk UI (hlavně pro testy a diagnostiku). */
export function jazykUI() {
  return JAZYK;
}

/**
 * Překlad UI textu. Klíčem je ČESKÝ TEXT.
 *
 * Pravidla (každé kvůli konkrétní pasti):
 *  • nestring (číslo, null, JSX) projde beze změny — volající obaluje i
 *    proměnné a pád by shodil celou obrazovku,
 *  • čeština se vrací rovnou — je to zdroj, ne překlad,
 *  • prázdný překlad ("") se bere jako chybějící; prázdno na obrazovce vypadá
 *    jako ztracená data, čeština ne.
 */
export function TL(text, jazyk) {
  if (typeof text !== "string") return text;
  /* JAZYK PARAMETREM MÁ PŘEDNOST. Kdo `lang` v dosahu má (komponenta z
   * `useLanguage()`, čistá funkce s parametrem), musí ho poslechnout — jinak
   * výsledek závisí na globálním stavu, což nejde otestovat ani použít pro dva
   * jazyky naráz (tisk v jazyce klienta). Globální hodnota je záloha pro místa,
   * kde `lang` v dosahu NENÍ: pomocné funkce, callbacky, moduly mimo strom. */
  const j = jazyk === undefined ? JAZYK : (JAZYKY.includes(jazyk) ? jazyk : "cs");
  if (j === "cs") return text;
  const radek = SLOVNIK[text];
  if (!radek) return text;
  const v = radek[j];
  return typeof v === "string" && v ? v : text;
}

/**
 * Překlad s VÝSLOVNĚ zadaným jazykem.
 *
 * `TL()` výš si jazyk bere z globálního stavu, což je správně pro React
 * komponenty (jazyk UI je jeden pro celou obrazovku). ČISTÉ FUNKCE ale
 * dostávají `lang` parametrem a musí ho poslechnout — jinak by výsledek
 * závisel na tom, co je zrovna nastavené v appce, a to se nedá otestovat
 * ani použít pro dva jazyky naráz (třeba tisk v jazyce klienta).
 * Pravidlo: máš `lang` v dosahu jako parametr → použij TLj.
 */
export function TLj(text, jazyk) {
  return TL(text, jazyk === undefined ? "cs" : jazyk);
}

/** Locale pro formátování data podle JAZYKA UI (ne podle měny). */
const LOCALE = { cs: "cs-CZ", sk: "sk-SK", en: "en-GB", uk: "uk-UA", vi: "vi-VN" };
export const localeUI = () => LOCALE[JAZYK] || LOCALE.cs;

export default TL;
